import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { BrainCircuit, RefreshCw, Activity, Hammer } from 'lucide-react';

const NAVY = '#293891';
const RED = '#E63329';
const LABELS = ['Python', 'SQL', 'Excel', 'Power BI', 'Tableau', 'Gen AI', 'JIRA', 'Agentic AI'];
const R = 31;

const NODES = LABELS.map((label, i) => {
  const a = ((-67.5 + i * 45) * Math.PI) / 180;
  return { label, x: 50 + R * Math.cos(a), y: 50 + R * Math.sin(a), color: i % 2 ? RED : NAVY };
});

function FlowLines() {
  return (
    <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full" aria-hidden="true">
      <circle cx="50" cy="50" r={R} fill="none" stroke="#D5DAEE" strokeWidth="0.25" />
      {NODES.map((n, i) => {
        const d = i % 2 ? `M${n.x} ${n.y} L50 50` : `M50 50 L${n.x} ${n.y}`;
        return (
          <g key={n.label}>
            <path d={d} className={`flow-line ${i % 2 ? 'rev' : ''}`} stroke={n.color} strokeOpacity="0.45" strokeWidth="0.35" fill="none" />
            <circle r="0.9" fill={n.color}>
              <animateMotion dur={`${2.4 + (i % 3) * 0.5}s`} begin={`${i * 0.3}s`} repeatCount="indefinite" path={d} />
            </circle>
          </g>
        );
      })}
    </svg>
  );
}

function Core() {
  return (
    <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" data-testid="hero-ai-core">
      <div className="relative w-20 h-20 md:w-24 md:h-24">
        <span className="core-ring absolute inset-0 rounded-full bg-[#E63329]/30" />
        <span className="core-ring d2 absolute inset-0 rounded-full bg-[#293891]/25" />
        <span className="orbit-spin absolute -inset-3 rounded-full border border-dashed border-[#293891]/40" />
        <div className="relative w-full h-full rounded-full bg-[#293891] text-white flex flex-col items-center justify-center shadow-[0_18px_40px_-12px_rgba(41,56,145,0.6)]">
          <BrainCircuit size={26} />
          <span className="text-[10px] font-bold tracking-[0.2em] mt-1">AI CORE</span>
        </div>
      </div>
    </div>
  );
}

function Node({ n, i }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.6 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5, delay: 0.3 + i * 0.08 }}
      className="absolute"
      style={{ left: `${n.x}%`, top: `${n.y}%` }}
    >
      <div
        data-testid={`hero-node-${n.label.toLowerCase().replace(/\s+/g, '-')}`}
        className="node-pill -translate-x-1/2 -translate-y-1/2 whitespace-nowrap rounded-full bg-white border-2 px-2.5 py-1 md:px-3.5 md:py-1.5 text-[11px] md:text-[13px] font-semibold"
        style={{ borderColor: n.color, color: n.color, '--glow': `${n.color}55`, animationDelay: `${i * 0.35}s` }}
      >
        {n.label}
      </div>
    </motion.div>
  );
}

function Widget({ pos, origin, float, delay, testId, children }) {
  return (
    <div className={`absolute z-20 ${pos} ${origin} max-[479px]:scale-[0.78]`}>
      <motion.div
        data-testid={testId}
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: [0, float, 0] }}
        transition={{ opacity: { duration: 0.6, delay }, y: { duration: 6, repeat: Infinity, ease: 'easeInOut', delay } }}
        className="bg-white/90 backdrop-blur border border-line rounded-2xl px-3.5 py-3 shadow-[0_16px_40px_-18px_rgba(41,56,145,0.35)] w-[148px]"
      >
        {children}
      </motion.div>
    </div>
  );
}

const WidgetTitle = ({ icon: Icon, label, color }) => (
  <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] font-bold" style={{ color }}>
    <Icon size={12} /> {label}
  </div>
);

const LOOP = ['Ask', 'Query', 'Visualise', 'Decide'];

function AnalystLoop() {
  const [step, setStep] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setStep((s) => (s + 1) % LOOP.length), 1400);
    return () => clearInterval(t);
  }, []);
  return (
    <>
      <WidgetTitle icon={RefreshCw} label="Analyst Loop" color={NAVY} />
      <div className="mt-2 grid grid-cols-2 gap-1">
        {LOOP.map((l, i) => (
          <span
            key={l}
            className={`text-[11px] font-semibold rounded-md px-2 py-1 text-center transition-colors duration-300 ${i === step ? 'bg-[#E63329] text-white' : 'bg-[#EEF0F8] text-[#293891]'}`}
          >
            {l}
          </span>
        ))}
      </div>
    </>
  );
}

function LiveData() {
  const [rows, setRows] = useState(12480);
  useEffect(() => {
    const t = setInterval(() => setRows((r) => r + Math.floor(Math.random() * 40 + 10)), 900);
    return () => clearInterval(t);
  }, []);
  return (
    <>
      <WidgetTitle icon={Activity} label="Live Data" color={RED} />
      <div className="mt-2 flex items-end gap-1 h-8">
        {[0, 1, 2, 3, 4, 5, 6].map((b) => (
          <span key={b} className="live-bar flex-1 h-full rounded-sm" style={{ background: b % 2 ? RED : NAVY, animationDelay: `${b * 0.18}s` }} />
        ))}
      </div>
      <div className="mt-1.5 text-[11px] text-[color:var(--muted)] whitespace-nowrap">
        <span className="font-display text-[14px] text-[#293891]" data-testid="hero-live-rows">{rows.toLocaleString('en-IN')}</span> rows live
      </div>
    </>
  );
}

function YouBuild() {
  return (
    <>
      <WidgetTitle icon={Hammer} label="You Build" color={NAVY} />
      <div className="mt-1.5 font-display text-[14px] text-[#293891] leading-tight">Sales Forecast Dashboard</div>
      <div className="mt-2 h-1.5 rounded-full bg-[#EEF0F8] overflow-hidden">
        <div className="build-fill h-full rounded-full bg-gradient-to-r from-[#293891] to-[#E63329]" />
      </div>
    </>
  );
}

export default function DataFlowNetwork() {
  return (
    <div className="relative w-full max-w-[540px] mx-auto aspect-square" data-testid="hero-data-flow-network">
      <span className="orbit-spin rev absolute inset-[4%] rounded-full border border-[#E63329]/15" />
      <FlowLines />
      <Core />
      {NODES.map((n, i) => (
        <Node key={n.label} n={n} i={i} />
      ))}
      <Widget pos="-top-6 -left-2 lg:-top-10 lg:-left-12" origin="origin-top-left" float={-10} delay={0.9} testId="hero-widget-analyst-loop">
        <AnalystLoop />
      </Widget>
      <Widget pos="-bottom-6 -right-2 lg:-bottom-10 lg:-right-6" origin="origin-bottom-right" float={10} delay={1.1} testId="hero-widget-live-data">
        <LiveData />
      </Widget>
      <Widget pos="-bottom-6 -left-2 lg:-bottom-10 lg:-left-12" origin="origin-bottom-left" float={-8} delay={1.3} testId="hero-widget-you-build">
        <YouBuild />
      </Widget>
    </div>
  );
}
